"use client";

import { useEffect, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TRIAL_TERMS_SHORT } from "@/lib/landing-constants";
import { DIAMOND_UPGRADE_LABEL, diamondUpgradeHref, isDiamondMember } from "@/lib/marketing/member-plan";
import { usePublicAuth } from "@/lib/public-auth";
import { trackMarketingCtaClick } from "@/utils/analytics";

export function StickyTrialCta() {
  const { subscriptions, isLoading } = usePublicAuth();
  const upgradeHref = isLoading ? null : diamondUpgradeHref(subscriptions);
  const onDiamond = !isLoading && isDiamondMember(subscriptions);
  const barRef = useRef<HTMLDivElement>(null);
  const [heroCtaGone, setHeroCtaGone] = useState(false);
  const [pricingReached, setPricingReached] = useState(false);

  useEffect(() => {
    const heroCta = document.querySelector("[data-hero-trial-cta]");
    const pricing = document.querySelector("[data-home-pricing]");
    if (!heroCta || !pricing) return;

    const heroObserver = new IntersectionObserver(([entry]) => {
      setHeroCtaGone(!entry.isIntersecting && entry.boundingClientRect.bottom < 0);
    });
    const pricingObserver = new IntersectionObserver(
      ([entry]) => {
        setPricingReached(entry.isIntersecting);
      },
      { rootMargin: "100000px 0px 0px 0px" },
    );
    heroObserver.observe(heroCta);
    pricingObserver.observe(pricing);
    return () => {
      heroObserver.disconnect();
      pricingObserver.disconnect();
    };
  }, []);

  const visible = heroCtaGone && !pricingReached && !isLoading && !onDiamond;

  useEffect(() => {
    if (!visible || !barRef.current) return;
    const root = document.documentElement;
    root.style.setProperty("--sticky-trial-cta-h", `${barRef.current.offsetHeight}px`);
    return () => {
      root.style.removeProperty("--sticky-trial-cta-h");
    };
  }, [visible]);

  return (
    <div
      ref={barRef}
      inert={!visible}
      className={`fixed bottom-0 inset-x-0 z-40 md:hidden transform-gpu transition-transform duration-500 motion-reduce:transition-none ${
        visible ? "translate-y-0" : "translate-y-[110%] pointer-events-none"
      }`}
      style={{ transitionTimingFunction: "var(--ease-spring)" }}
    >
      <div
        className="bg-background/80 backdrop-blur-xl backdrop-saturate-150 border-t border-white/10 px-4 pt-3 shadow-[0_-4px_20px_rgba(0,0,0,0.3)]"
        style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom, 0.75rem))" }}
      >
        <Button
          asChild
          size="lg"
          className="w-full btn-toplight press-scale bg-accent-value hover:bg-accent-value-strong text-zinc-950 rounded-xl font-semibold"
        >
          <a
            href={upgradeHref ?? "#pricing"}
            data-plan-chooser={upgradeHref ? undefined : "home_sticky_mobile_bar"}
            onClick={() =>
              trackMarketingCtaClick({
                surface: "home_sticky_mobile_bar",
                cta: upgradeHref ? "upgrade_diamond" : "start_trial",
                destination: upgradeHref ? "/pricing" : "#pricing",
              })
            }
          >
            <span className="flex items-center gap-2">
              {upgradeHref ? DIAMOND_UPGRADE_LABEL : "Try 5-Day Free Trial"}
              <ArrowRight className="size-4" />
            </span>
          </a>
        </Button>
        {!upgradeHref && (
          <p className="mt-1.5 text-center text-xs text-muted-foreground">
            {TRIAL_TERMS_SHORT}
          </p>
        )}
      </div>
    </div>
  );
}
